import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { CANCEL_ANIMATION_DELAY, MODAL_DIALOG_WIDTH } from 'modules/core/constants';
import { ModalTheme, RequestStatus, TableName, ButtonVariant } from 'modules/core/enums';
import { SetFieldValue } from 'modules/core/types/form';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { IField, IOption, IUser } from 'modules/core/types';
import { FormRow, Input } from 'modules/core/components';
import { getSubventionYearOptions, getControlImages } from 'store/selectors/controls.selector';
import { IDealSourceData, IDealSourceDataFlags } from 'modules/core/types/dealSource';
import { getAddUserVehicleFlags, getUpdateUserVehicleFlags, getVehicleState } from 'store/selectors/vehicle.selector';
import { getUser } from 'store/selectors/auth.selector';
import { ModalDialog } from 'components/ModalDialog';
import { AppDispatch } from 'store/store';
import { createUserVehicleThunk, updateUserVehicleThunk, getAIVinDecodedDataThunk } from 'actions/vehicleActions';
import {
  resetAddUserVehicleFlags,
  resetUpdateUserVehicleFlags,
  resetCreatedUserVehicle,
  setCreatedUserVehicleTableName,
  setCreatedUserVehicleLenderId,
} from 'store/reducers/vehicle.reducer';
import { vehicleApi } from 'api';
import { AISparkles } from 'modules/core/components/FormRow/styles';

import {
  ADD_VEHICLE_FORM_INITIAL_VALUES,
  AI_DECODABLE_FIELDS,
  AI_DECODABLE_FIELDS_SPARKLES_INITIAL_VALUES,
  INITIAL_DROPDOWN_VALUE,
} from './consts/consts';
import { AIMessage, FormControlFeedback, Panel, UnlockInventoryButton } from './styles';
import { ADD_VEHICLE_FIELDS } from './consts/fieldConfig.const';
import { ADD_VEHICLE_FIELDS_NAMES, ADD_VEHICLE_FORM_PREFIX } from './consts/fieldNames.const';
import { AIResponseMapper, getCurrentVehicleModelsFilterOptions } from './AddVehicleDialog.service';
import { IAIVinDecodedData } from './types';
import { LOAN_VARIABLES_FIELDS_NAMES } from '../LoanVariablesDialog/consts/fieldNames.const';
import { getNormalizedDirty } from '../utils';

interface IAddVehicleDialog {
  show: boolean;
  onHide: () => void;
  onHideAnotherModal?: () => void;
  tier?: number | string;
  fromModal?: boolean;
  lenderName?: string;
  tableName: TableName;
  isDealSourceVehicle?: boolean;
  initialValues?: any;
  dealSourceData?: IDealSourceData;
  isMakeItWork?: boolean;
  setDealSourceData?: Function;
  dealSourceDataFlags?: IDealSourceDataFlags;
  setExternalValue?: SetFieldValue;
  updateCallback?: Function;
  overrideFormHistoryValues?: Function;
  currentLenderId?: string;
}

export const AddVehicleDialog: FC<IAddVehicleDialog> = ({
  show,
  onHide,
  tier,
  fromModal = false,
  onHideAnotherModal,
  lenderName,
  currentLenderId,
  tableName,
  isDealSourceVehicle = false,
  initialValues = ADD_VEHICLE_FORM_INITIAL_VALUES,
  dealSourceData,
  isMakeItWork = false,
  setDealSourceData,
  setExternalValue,
  dealSourceDataFlags,
  updateCallback,
  overrideFormHistoryValues,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const defaultFormValues = { addVehicleForm: initialValues };
  const formMethods = useForm({
    mode: 'onSubmit',
    shouldUnregister: false,
    defaultValues: defaultFormValues,
  });
  const {
    getValues,
    reset,
    setValue,
    trigger,
    formState: { isDirty, dirtyFields, errors },
  } = formMethods;
  const normalizedDirty = getNormalizedDirty(formMethods, defaultFormValues);

  const subventionYearOptions = useSelector(getSubventionYearOptions);
  const addUserVehicleFlags = useSelector(getAddUserVehicleFlags);
  const updateUserVehicleFlags = useSelector(getUpdateUserVehicleFlags);
  const { vehicleMakes, vehicleModels } = useSelector(getVehicleState);
  const controlImages = useSelector(getControlImages);
  const user = useSelector(getUser) as IUser;
  const unlockInventoryUrl = controlImages.unlockInventoryUrl;
  const isMissingRequiredInformationDialog =
    initialValues !== ADD_VEHICLE_FORM_INITIAL_VALUES && isDealSourceVehicle && Boolean(initialValues.id);
  const shouldShowUpgradeButton =
    Boolean(unlockInventoryUrl) && !user?.isWithInventory && !isMissingRequiredInformationDialog;
  const [isFieldsAnimated, setIsFieldsAnimated] = useState<boolean>(false);
  const [isSpinerAnimated, setIsSpinerAnimated] = useState<boolean>(false);
  const [isDecodedByAI, setIsDecodedByAI] = useState<boolean>(false);

  const [sparklesEnabled, setSparklesEnabled] = useState<Record<string, Boolean>>(
    AI_DECODABLE_FIELDS_SPARKLES_INITIAL_VALUES,
  );

  const vehicleMakeOptions = useMemo(() => vehicleMakes.slice(3), [vehicleMakes]);
  const isRac = tableName === TableName.RAC;
  const isLender = tableName === TableName.LENDER;
  const vehicleId = initialValues.id;
  const isInvalidVin = errors?.addVehicleForm && Object.keys(errors.addVehicleForm).includes('vin');
  const cancelButtonText = t('common.buttons.cancel');

  const { vin, condition, make, model, trim, year, bookValue, mileage, retailPrice } = getValues().addVehicleForm;

  const currentModels = getCurrentVehicleModelsFilterOptions(make.title, vehicleModels);

  const mapOptionsByField: Map<string, IOption[]> = useMemo(
    () =>
      new Map([
        [ADD_VEHICLE_FIELDS_NAMES.MAKE, vehicleMakeOptions],
        [ADD_VEHICLE_FIELDS_NAMES.MODEL, currentModels],
        [ADD_VEHICLE_FIELDS_NAMES.YEAR, subventionYearOptions],
      ]),
    [vehicleMakeOptions, currentModels, subventionYearOptions],
  );

  const onSubmit = useCallback(async () => {
    dispatch(resetCreatedUserVehicle());
    dispatch(setCreatedUserVehicleTableName(tableName));

    if (isLender && currentLenderId) {
      dispatch(setCreatedUserVehicleLenderId(currentLenderId));
    }

    const params: any = {
      vin: vin,
      condition: condition.value,
      make: make.value,
      model: model.value,
      trim: trim,
      year: year.value,
      book_value: bookValue,
      mileage: mileage,
      retail_price: retailPrice,
      tier: tier !== undefined ? tier.toString() : undefined,
      is_rac: isRac,
      is_lender: isLender,
      lender_name: isLender ? lenderName : undefined,
      ...(isRac && isMakeItWork && { deal_date: dealSourceData?.submitted_date_time }),
    };

    if (vehicleId) {
      params.id = vehicleId;

      const updateUserVehicleResponse = await dispatch(updateUserVehicleThunk(params));
      const isUpdateRejected = updateUserVehicleResponse?.meta?.requestStatus === RequestStatus.REJECTED;

      if (!isUpdateRejected && overrideFormHistoryValues && setExternalValue && setDealSourceData) {
        setExternalValue(LOAN_VARIABLES_FIELDS_NAMES.MILEAGE, params.mileage);
        setExternalValue(LOAN_VARIABLES_FIELDS_NAMES.RETAIL_PRICE, params.retail_price);
        setExternalValue(LOAN_VARIABLES_FIELDS_NAMES.GUIDEBOOK_VALUE, params.book_value);

        overrideFormHistoryValues({
          mileage: params.mileage,
          retailPrice: params.retail_price,
          guidebookValue: params.book_value,
        });

        dispatch(
          setDealSourceData({
            ...dealSourceData,
            sale_vehicle: {
              ...dealSourceData?.sale_vehicle,
              vin: params.vin,
              make: params.make,
              model: params.model,
              trim: params.trim,
              year: params.year,
              mileage: params.mileage,
              retail_price: params.retail_price,
              book_value: params.book_value,
            },
          }),
        );
      }
    } else {
      dispatch(createUserVehicleThunk(params));
    }
  }, [
    bookValue,
    currentLenderId,
    dispatch,
    lenderName,
    make.value,
    mileage,
    model.value,
    isLender,
    isRac,
    retailPrice,
    tableName,
    tier,
    vin,
    condition,
    year.value,
    trim,
    vehicleId,
    dealSourceData,
    setExternalValue,
    setDealSourceData,
    overrideFormHistoryValues,
    isMakeItWork,
  ]);

  const vinBlurCallback = useCallback(
    async (event) => {
      const relatedTarget = event?.relatedTarget;

      if (relatedTarget instanceof HTMLButtonElement && relatedTarget.textContent === cancelButtonText) {
        return;
      }

      const { vin } = getValues().addVehicleForm;
      const isValidVin = await trigger(ADD_VEHICLE_FIELDS_NAMES.VIN);
      const isVinChanges = dirtyFields?.addVehicleForm && 'vin' in dirtyFields?.addVehicleForm;
      const errorFields =
        errors?.addVehicleForm && Object.keys(errors.addVehicleForm).map((key) => `addVehicleForm.${key}`);
      const fieldsToTriggerValidation = [
        ADD_VEHICLE_FIELDS_NAMES.MAKE,
        ADD_VEHICLE_FIELDS_NAMES.MODEL,
        ADD_VEHICLE_FIELDS_NAMES.TRIM,
        ADD_VEHICLE_FIELDS_NAMES.YEAR,
        ADD_VEHICLE_FIELDS_NAMES.MILEAGE,
      ];

      if (isValidVin && isVinChanges) {
        setIsFieldsAnimated(true);
        const spinerTimeout = setTimeout(() => {
          setIsSpinerAnimated(true);
        }, CANCEL_ANIMATION_DELAY / 4);

        let sparklesState = { ...AI_DECODABLE_FIELDS_SPARKLES_INITIAL_VALUES };

        setSparklesEnabled(sparklesState);

        const response = await dispatch(getAIVinDecodedDataThunk(vin));
        const mapper = new AIResponseMapper(
          mapOptionsByField.get(ADD_VEHICLE_FIELDS_NAMES.MAKE)!,
          mapOptionsByField.get(ADD_VEHICLE_FIELDS_NAMES.YEAR)!,
          vehicleModels,
        );

        const newMappedData = mapper.mapFields(response.payload as IAIVinDecodedData);

        Object.entries(newMappedData).forEach(([key, value]) => {
          if (value && value !== INITIAL_DROPDOWN_VALUE) {
            sparklesState[`${ADD_VEHICLE_FORM_PREFIX}.${key}`] = true;
          }
        });
        setSparklesEnabled(sparklesState);

        reset({
          addVehicleForm: {
            ...getValues().addVehicleForm,
            ...newMappedData,
          },
        });

        clearTimeout(spinerTimeout);
        setIsDecodedByAI(true);
        setIsSpinerAnimated(false);
        setIsFieldsAnimated(false);

        if (errorFields && errorFields.length > 0) {
          fieldsToTriggerValidation.push(...errorFields);
        }

        trigger(fieldsToTriggerValidation);
      }
    },
    [getValues, trigger, dirtyFields, reset, mapOptionsByField, vehicleModels, dispatch, errors, cancelButtonText],
  );

  const onTriggerCallback = useCallback(
    (name: string) => {
      let newSparklesValues = { ...sparklesEnabled };

      newSparklesValues[name] = false;

      if (name === ADD_VEHICLE_FIELDS_NAMES.MAKE) {
        newSparklesValues[ADD_VEHICLE_FIELDS_NAMES.MODEL] = false;
      }

      setSparklesEnabled(newSparklesValues);
      trigger(name);
    },
    [sparklesEnabled, trigger],
  );

  const renderFields = useCallback(
    (fields: IField[]) =>
      fields.map(({ name, component: Component, label, controlProps, isVerticalAlign }: IField) => {
        const options = mapOptionsByField.get(name) || (controlProps?.options as any[]);
        const nameKey = name.split('.')[1];
        const isInvalid = errors?.addVehicleForm && nameKey in errors.addVehicleForm;

        let isDisabled = Boolean(controlProps?.disabled);
        let showAISparkles = !!sparklesEnabled[name];
        let isComponentAnimated = false;
        let isComponentSpinerAnimated = false;

        if (AI_DECODABLE_FIELDS.includes(name)) {
          isComponentAnimated = isFieldsAnimated;
          isComponentSpinerAnimated = isSpinerAnimated;
        }

        if (AI_DECODABLE_FIELDS.includes(name) || name === ADD_VEHICLE_FIELDS_NAMES.VIN) {
          isDisabled = isFieldsAnimated;
        }

        if (name === ADD_VEHICLE_FIELDS_NAMES.MODEL) {
          isDisabled = !make.title || isFieldsAnimated;
        }

        const isVinPatternError =
          name === ADD_VEHICLE_FIELDS_NAMES.VIN && isInvalid && errors.addVehicleForm[nameKey]?.type === 'pattern';
        const componentPatternError =
          Component === Input ? 'addVehicleModal.errorMessages.input' : 'addVehicleModal.errorMessages.dropdown';
        const errorPattern = isVinPatternError ? 'addVehicleModal.vin.errorMessage' : componentPatternError;
        const errorMessage = isInvalid && !isDisabled ? t(errorPattern, { label: label }) : undefined;

        return (
          <FormRow
            key={name}
            label={label}
            labelWithoutAsterisk={Boolean(controlProps?.labelWithoutAsterisk)}
            disabled={isDisabled}
            required={Boolean(controlProps?.isRequired)}
            isVerticalAlign={isVerticalAlign}
            showAISparkles={showAISparkles}
            control={
              <>
                <Component
                  {...controlProps}
                  name={name}
                  disabled={isDisabled}
                  isInvalid={isInvalid && !isDisabled}
                  options={options}
                  changeCallback={name === ADD_VEHICLE_FIELDS_NAMES.VIN ? undefined : () => onTriggerCallback(name)}
                  blurCallback={name === ADD_VEHICLE_FIELDS_NAMES.VIN ? vinBlurCallback : undefined}
                  isComponentAnimated={isComponentAnimated}
                  isSpinerAnimated={isComponentSpinerAnimated}
                />
                {errorMessage && <FormControlFeedback type="invalid">{errorMessage}</FormControlFeedback>}
              </>
            }
          />
        );
      }),
    [
      make.title,
      mapOptionsByField,
      isFieldsAnimated,
      isSpinerAnimated,
      errors,
      t,
      vinBlurCallback,
      sparklesEnabled,
      onTriggerCallback,
    ],
  );

  const handleHide = useCallback(() => {
    vehicleApi.cancelGetAIVinDecodedData();
    onHide();
    reset({ addVehicleForm: ADD_VEHICLE_FORM_INITIAL_VALUES });
    setSparklesEnabled(AI_DECODABLE_FIELDS_SPARKLES_INITIAL_VALUES);
    setIsFieldsAnimated(false);
    setIsSpinerAnimated(false);
    setIsDecodedByAI(false);
  }, [reset, onHide]);

  const onSubmitCallback = useCallback(async () => {
    if (!isFieldsAnimated) {
      const isValid = await trigger();

      if (isValid) onSubmit();
    }
  }, [onSubmit, isFieldsAnimated, trigger]);

  const getTitle = useCallback(() => {
    if (isMissingRequiredInformationDialog) {
      return t('addVehicleModal.invalidVinOrTrimTitle').toUpperCase();
    }

    return t('addVehicleModal.title');
  }, [isMissingRequiredInformationDialog, t]);

  const handleUnlockInventoryClick = useCallback(() => {
    if (unlockInventoryUrl) {
      window.open(unlockInventoryUrl, '_blank');
    }
  }, [unlockInventoryUrl]);

  useEffect(() => {
    if (make.value && isDirty) {
      setValue(ADD_VEHICLE_FIELDS_NAMES.MODEL, {
        title: undefined,
        value: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [make.value, setValue]);

  useEffect(() => {
    if (addUserVehicleFlags.isLoaded) {
      handleHide();

      if (onHideAnotherModal) onHideAnotherModal();
      dispatch(resetAddUserVehicleFlags());
      reset({ addVehicleForm: ADD_VEHICLE_FORM_INITIAL_VALUES });
    }
  }, [addUserVehicleFlags.isLoaded, dispatch, fromModal, handleHide, onHideAnotherModal, reset]);

  useEffect(() => {
    if (updateUserVehicleFlags.isLoaded && (dealSourceDataFlags?.isUpdated || updateUserVehicleFlags.isInvalid)) {
      updateCallback?.({ target: { name: ADD_VEHICLE_FORM_PREFIX } });
      handleHide();
      dispatch(resetUpdateUserVehicleFlags());
      reset({ addVehicleForm: ADD_VEHICLE_FORM_INITIAL_VALUES });
    }
  }, [
    updateUserVehicleFlags.isLoaded,
    updateUserVehicleFlags.isInvalid,
    dealSourceDataFlags?.isUpdated,
    dispatch,
    handleHide,
    reset,
    updateCallback,
  ]);

  useEffect(() => {
    if (vehicleId && show) {
      trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, show]);

  return (
    <FormProvider {...formMethods}>
      <ModalDialog
        show={show}
        onHide={!vehicleId ? handleHide : undefined}
        onSubmitModal={onSubmitCallback}
        keyboard={false}
        title={getTitle()}
        modalSize={MODAL_DIALOG_WIDTH.CUSTOM.PX_425}
        modalTheme={ModalTheme.DEFAULT}
        submitButtonTitle={t('common.buttons.save')}
        cancelButtonTitle={!vehicleId ? cancelButtonText : undefined}
        showCloseButton={!vehicleId}
        isDisabled={addUserVehicleFlags.isFetching || (vehicleId && isInvalidVin)}
        additionalButton={
          shouldShowUpgradeButton ? (
            <UnlockInventoryButton
              title={t('common.buttons.upgrade')}
              onClick={handleUnlockInventoryClick}
              buttontheme={ButtonVariant.OUTLINE}
            >
              {t('common.buttons.upgrade')}
            </UnlockInventoryButton>
          ) : null
        }
        centered
        isDirty={!vehicleId && normalizedDirty}
      >
        <Panel>{renderFields(ADD_VEHICLE_FIELDS)}</Panel>
        <AIMessage style={{ display: isDecodedByAI ? 'block' : 'none' }}>
          <AISparkles />
          {t('addVehicleModal.decodedByAI')}
        </AIMessage>
      </ModalDialog>
    </FormProvider>
  );
};
